import React, { useEffect, useRef, useState } from 'react';
import { hasOccurence, hasOccurenceViewed } from '@utils/occurrence';
import WhatsAppButton from '@components/ui/WhatsAppButton';
import useGlobals from '@hooks/useGlobals';
import {
  FuelGridContainer,
  FuelGridItem,
  FuelInteractiveIcon,
} from '@frete.com/fuel-react';
import DropDown from '@components/ui/DropDown';
import { FuelFlex } from '@frete.com/fuel-station-react';
import LocationWithStatus from '@components/ui/Location/LocationWithStatus';
import TruckerLastLocation from '@components/ui/Trucker/TruckerLastLocation';
import { hasOpacity } from '@helpers/journeys/status';
import { getTextToContactTruckerWhatsApp } from '@helpers/journeys/whatsappTalk';
import { handleTruncatedTextInSmallScreens } from '@utils/responsive';
import { JourneyListItemPropsInterface } from '../../JourneyListType';
import KebabList from '../../KebabList';
import * as Styled from './JourneysAllTabDesktopStyles';
import ActionsButtons from '../ActionsButtons';

function JourneysAllTabDesktop(props: JourneyListItemPropsInterface) {
  const {
    id,
    trucker: { name, avatar, phone, lastLocation, depositFee },
    address,
    handleClick,
    handlerModalDialogue,
    onCallbackSentWhatsApp,
    status,
    hasBorder,
    isMarker,
    occurrence,
  } = props;
  const [open, setOpen] = useState(false);
  const journeyRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);
  const { user } = useGlobals();
  const fullName = name.split(' ');
  const firstName = fullName ? fullName[0] : '';
  const messageWhatsApp = getTextToContactTruckerWhatsApp({
    truckerName: firstName,
    companyName: user?.data?.company?.name || '',
    userName: user?.data?.name.split(' ')[0] || '',
    type: 'journey',
    id,
  });

  function handleMouseOver(idJourney: string) {
    const marker = document.getElementById(`marker-journeyId-${idJourney}`);
    if (marker && marker.parentNode instanceof HTMLElement) {
      marker.parentNode.classList.add('hover-journey-item-marker');
      window.dispatchEvent(
        new CustomEvent('mapCenterByCoordinates', {
          detail: {
            lastLocation,
          },
        }),
      );
    }
  }

  function handleMouseLeave(idJourney: string) {
    const marker = document.getElementById(`marker-journeyId-${idJourney}`);
    if (marker && marker.parentNode instanceof HTMLElement) {
      marker.parentNode.classList.remove('hover-journey-item-marker');
    }
  }

  useEffect(() => {
    if (isMarker && journeyRef.current) {
      journeyRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [isMarker]);

  return (
    <Styled.WrapperStyledFuelGridContainer
      data-testid="listItemJourneysAllTabDesktop"
      onClick={(event) => handleClick(event, props)}
      onMouseOver={() => handleMouseOver(id)}
      onMouseLeave={() => handleMouseLeave(id)}
      hasBorder={hasBorder}
      ref={journeyRef}
    >
      <Styled.WrapperRow>
        <FuelGridContainer gapRow={32} style={{ columnGap: '16px' }}>
          <FuelGridItem sm={3} md={3} lg={4} xlg={4}>
            <Styled.WrapperMarker isMarker={!!isMarker}>
              <TruckerLastLocation
                avatar={avatar}
                name={name}
                notification={{
                  isShow:
                    hasOccurence(occurrence) && !hasOccurenceViewed(occurrence),
                  total: 1,
                }}
                depositFee={depositFee}
                origin={address.pickup}
                destination={address.delivery}
                hasOpacity={hasOpacity(status)}
              />
            </Styled.WrapperMarker>
          </FuelGridItem>

          <FuelGridItem sm={3} md={3} lg={3} xlg={4}>
            <LocationWithStatus
              location={{
                cityName: handleTruncatedTextInSmallScreens(
                  lastLocation.city.name,
                  6,
                ),
                stateAcronym: lastLocation.state.acronym,
                updateAt: lastLocation.timestamp,
              }}
              status={{
                slug:
                  status.slug === 'canceled'
                    ? `${status.slug}:${status.by}`
                    : status.slug,
                updateAt: status.updatedAt,
              }}
            />
          </FuelGridItem>
          <FuelGridItem sm={4} md={3} lg={5} xlg={4}>
            <Styled.WrapperColumnAction>
              <FuelFlex defaultProps={{ alignItems: 'center', gap: 'km2' }}>
                <ActionsButtons {...props} />
                <FuelFlex
                  defaultProps={{
                    alignItems: 'center',
                    gap: 'km1',
                    justifyContent: 'space-between',
                  }}
                >
                  {phone && (
                    <div>
                      <WhatsAppButton
                        phone={phone}
                        message={messageWhatsApp}
                        onCallbackSentWhatsApp={() =>
                          onCallbackSentWhatsApp?.(props)
                        }
                      />
                    </div>
                  )}
                  <div data-testid="container-kebab-list" ref={buttonRef}>
                    <FuelInteractiveIcon
                      icon="kebab-vertical"
                      size="large"
                      iconStyle="filled"
                      onClick={() => setOpen(!open)}
                      data-testid={`label-button-${id}`}
                    />
                    <DropDown
                      open={open}
                      width="360px"
                      onClose={() => setOpen(false)}
                      outsideRefs={[buttonRef]}
                    >
                      <KebabList
                        openModalConfirm={() => handlerModalDialogue?.(props)}
                        {...props}
                      />
                    </DropDown>
                  </div>
                </FuelFlex>
              </FuelFlex>
            </Styled.WrapperColumnAction>
          </FuelGridItem>
        </FuelGridContainer>
      </Styled.WrapperRow>
    </Styled.WrapperStyledFuelGridContainer>
  );
}

export default JourneysAllTabDesktop;
